import ThemedSelect from '../../common/ThemedSelect';
import React, { useState, useEffect, useCallback } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  BarChart,
  ComposedChart,
  Line,
  Bar,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import api from '../../../services/weighbridgeComplianceApi';
import { getToken, useThemeTokens } from '../../../utils/themeTokens';

// ─── Constants ───────────────────────────────────────────────────────────────

const TEAL = '#45B7AA';
const EMERALD = '#10b981';
const RED = '#ef4444';
const SLATE = '#64748b';
const AMBER = '#f59e0b';

const PERIOD_OPTIONS = [
  { value: 3, label: '3 months' },
  { value: 6, label: '6 months' },
  { value: 12, label: '12 months' },
  { value: 24, label: '24 months' },
];

// ─── Shared tooltip style ─────────────────────────────────────────────────────

const tooltipStyle = {
  backgroundColor: 'var(--surface-raised)',
  border: '1px solid var(--border)',
  borderRadius: '0.5rem',
  fontSize: '0.8125rem',
  fontFamily: 'DM Sans, sans-serif',
};

const tooltipLabelStyle = {
  fontWeight: 600,
  color: 'var(--text-strong)',
  marginBottom: '4px',
};

// ─── Card wrapper ─────────────────────────────────────────────────────────────

const ChartCard = ({ title, loading, children }) => (
  <div className="bg-surface rounded-xl shadow-sm border border-slate-200 p-6">
    <h3
      className="text-base font-semibold text-slate-800 mb-5"
      style={{ fontFamily: 'DM Sans, sans-serif' }}
    >
      {title}
    </h3>
    {loading ? (
      <div className="flex items-center justify-center h-[300px] text-slate-400 text-sm">
        Loading...
      </div>
    ) : (
      children
    )}
  </div>
);

// ─── Custom tooltip formatters ────────────────────────────────────────────────

const ComplianceTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload ?? {};
  return (
    <div style={tooltipStyle} className="px-3 py-2 shadow-md">
      <p style={tooltipLabelStyle}>{label}</p>
      <p className="text-slate-600 text-xs">
        Compliance:{' '}
        <span className="font-semibold" style={{ color: TEAL }}>
          {d.compliance_pct?.toFixed(1)}%
        </span>
      </p>
      <p className="text-slate-600 text-xs">
        Compliant: {d.compliant_assets} / {d.total_assets} assets
      </p>
    </div>
  );
};

const MaintenanceTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload ?? {};
  return (
    <div style={tooltipStyle} className="px-3 py-2 shadow-md">
      <p style={tooltipLabelStyle}>{label}</p>
      <p className="text-slate-600 text-xs">
        Completed:{' '}
        <span className="font-semibold" style={{ color: EMERALD }}>
          {d.completed}
        </span>
      </p>
      <p className="text-slate-600 text-xs">
        Overdue:{' '}
        <span className="font-semibold" style={{ color: RED }}>
          {d.overdue}
        </span>
      </p>
      <p className="text-slate-600 text-xs">Total items: {d.total_items}</p>
      {d.total_cost != null && (
        <p className="text-slate-600 text-xs">
          Cost:{' '}
          <span className="font-semibold" style={{ color: AMBER }}>
            R {Number(d.total_cost).toLocaleString()}
          </span>
        </p>
      )}
    </div>
  );
};

const NcrTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload ?? {};
  return (
    <div style={tooltipStyle} className="px-3 py-2 shadow-md">
      <p style={tooltipLabelStyle}>{label}</p>
      <p className="text-slate-600 text-xs">
        Raised:{' '}
        <span className="font-semibold" style={{ color: SLATE }}>
          {d.raised}
        </span>
      </p>
      <p className="text-slate-600 text-xs">
        Resolved:{' '}
        <span className="font-semibold" style={{ color: EMERALD }}>
          {d.resolved}
        </span>
      </p>
      <p className="text-slate-600 text-xs">
        Critical:{' '}
        <span className="font-semibold" style={{ color: RED }}>
          {d.critical}
        </span>
      </p>
      {d.avg_resolution_days != null && (
        <p className="text-slate-600 text-xs">
          Avg resolution:{' '}
          <span className="font-semibold" style={{ color: AMBER }}>
            {d.avg_resolution_days?.toFixed(1)} days
          </span>
        </p>
      )}
    </div>
  );
};

// ─── Sub-charts ───────────────────────────────────────────────────────────────

const ComplianceChart = ({ data, loading }) => (
  <ChartCard title="Compliance % Over Time" loading={loading}>
    <ResponsiveContainer width="100%" height={300}>
      <ComposedChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="complianceFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={TEAL} stopOpacity={0.18} />
            <stop offset="95%" stopColor={TEAL} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={getToken('--border-subtle')} />
        <XAxis
          dataKey="month"
          tick={{ fontSize: 12, fill: getToken('--text-muted'), fontFamily: 'DM Sans, sans-serif' }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          domain={[0, 100]}
          tickFormatter={(v) => `${v}%`}
          tick={{ fontSize: 12, fill: getToken('--text-muted'), fontFamily: 'DM Sans, sans-serif' }}
          axisLine={false}
          tickLine={false}
          width={42}
        />
        <Tooltip content={<ComplianceTooltip />} />
        <Legend
          iconType="circle"
          iconSize={8}
          wrapperStyle={{
            fontSize: '0.8125rem',
            fontFamily: 'DM Sans, sans-serif',
            paddingTop: '12px',
          }}
        />
        <Area
          type="monotone"
          dataKey="compliance_pct"
          fill="url(#complianceFill)"
          stroke="none"
          name="Compliance %"
        />
        <Line
          type="monotone"
          dataKey="compliance_pct"
          stroke={TEAL}
          strokeWidth={2.5}
          dot={{ r: 3, fill: TEAL, strokeWidth: 0 }}
          activeDot={{ r: 5, fill: TEAL }}
          name="Compliance %"
        />
      </ComposedChart>
    </ResponsiveContainer>
  </ChartCard>
);

const MaintenanceChart = ({ data, loading }) => (
  <ChartCard title="Maintenance Activity" loading={loading}>
    <ResponsiveContainer width="100%" height={300}>
      <ComposedChart data={data} margin={{ top: 4, right: 48, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={getToken('--border-subtle')} />
        <XAxis
          dataKey="month"
          tick={{ fontSize: 12, fill: getToken('--text-muted'), fontFamily: 'DM Sans, sans-serif' }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          yAxisId="left"
          tick={{ fontSize: 12, fill: getToken('--text-muted'), fontFamily: 'DM Sans, sans-serif' }}
          axisLine={false}
          tickLine={false}
          width={36}
          allowDecimals={false}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          tick={{ fontSize: 12, fill: getToken('--text-muted'), fontFamily: 'DM Sans, sans-serif' }}
          axisLine={false}
          tickLine={false}
          width={56}
          tickFormatter={(v) => `R ${(v / 1000).toFixed(0)}k`}
        />
        <Tooltip content={<MaintenanceTooltip />} />
        <Legend
          iconType="square"
          iconSize={8}
          wrapperStyle={{
            fontSize: '0.8125rem',
            fontFamily: 'DM Sans, sans-serif',
            paddingTop: '12px',
          }}
        />
        <Bar
          yAxisId="left"
          dataKey="completed"
          stackId="a"
          fill={EMERALD}
          name="Completed"
          radius={[0, 0, 0, 0]}
        />
        <Bar
          yAxisId="left"
          dataKey="overdue"
          stackId="a"
          fill={RED}
          name="Overdue"
          radius={[3, 3, 0, 0]}
        />
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="total_cost"
          stroke={AMBER}
          strokeWidth={2}
          dot={{ r: 3, fill: AMBER, strokeWidth: 0 }}
          activeDot={{ r: 5, fill: AMBER }}
          name="Total Cost"
        />
      </ComposedChart>
    </ResponsiveContainer>
  </ChartCard>
);

const NcrChart = ({ data, loading }) => (
  <ChartCard title="NCR Trends" loading={loading}>
    <ResponsiveContainer width="100%" height={300}>
      <ComposedChart data={data} margin={{ top: 4, right: 48, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={getToken('--border-subtle')} />
        <XAxis
          dataKey="month"
          tick={{ fontSize: 12, fill: getToken('--text-muted'), fontFamily: 'DM Sans, sans-serif' }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          yAxisId="left"
          tick={{ fontSize: 12, fill: getToken('--text-muted'), fontFamily: 'DM Sans, sans-serif' }}
          axisLine={false}
          tickLine={false}
          width={36}
          allowDecimals={false}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          tick={{ fontSize: 12, fill: getToken('--text-muted'), fontFamily: 'DM Sans, sans-serif' }}
          axisLine={false}
          tickLine={false}
          width={48}
          tickFormatter={(v) => `${v}d`}
        />
        <Tooltip content={<NcrTooltip />} />
        <Legend
          iconType="square"
          iconSize={8}
          wrapperStyle={{
            fontSize: '0.8125rem',
            fontFamily: 'DM Sans, sans-serif',
            paddingTop: '12px',
          }}
        />
        <Bar yAxisId="left" dataKey="raised" fill={SLATE} name="Raised" radius={[3, 3, 0, 0]} />
        <Bar
          yAxisId="left"
          dataKey="resolved"
          fill={EMERALD}
          name="Resolved"
          radius={[3, 3, 0, 0]}
        />
        <Bar yAxisId="left" dataKey="critical" fill={RED} name="Critical" radius={[3, 3, 0, 0]} />
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="avg_resolution_days"
          stroke={AMBER}
          strokeWidth={2}
          dot={{ r: 3, fill: AMBER, strokeWidth: 0 }}
          activeDot={{ r: 5, fill: AMBER }}
          name="Avg Resolution (days)"
        />
      </ComposedChart>
    </ResponsiveContainer>
  </ChartCard>
);

// ─── Main component ───────────────────────────────────────────────────────────

const ComplianceTrends = () => {
  // Subscribes the whole subtree to theme changes so the getToken() reads in the
  // chart components below re-evaluate when the theme flips.
  useThemeTokens();
  const [months, setMonths] = useState(6);

  const [complianceData, setComplianceData] = useState([]);
  const [maintenanceData, setMaintenanceData] = useState([]);
  const [ncrData, setNcrData] = useState([]);

  const [loadingCompliance, setLoadingCompliance] = useState(true);
  const [loadingMaintenance, setLoadingMaintenance] = useState(true);
  const [loadingNcr, setLoadingNcr] = useState(true);

  const fetchAll = useCallback(async (selectedMonths) => {
    setLoadingCompliance(true);
    setLoadingMaintenance(true);
    setLoadingNcr(true);

    const [compRes, maintRes, ncrRes] = await Promise.allSettled([
      api.trends.compliance({ months: selectedMonths }),
      api.trends.maintenance({ months: selectedMonths }),
      api.trends.ncr({ months: selectedMonths }),
    ]);

    if (compRes.status === 'fulfilled') setComplianceData(compRes.value?.data || []);
    setLoadingCompliance(false);

    if (maintRes.status === 'fulfilled') setMaintenanceData(maintRes.value?.data || []);
    setLoadingMaintenance(false);

    if (ncrRes.status === 'fulfilled') setNcrData(ncrRes.value?.data || []);
    setLoadingNcr(false);
  }, []);

  useEffect(() => {
    fetchAll(months);
  }, [months, fetchAll]);

  return (
    <div className="space-y-6">
      {/* Period selector */}
      <div className="flex items-center gap-3">
        <label
          htmlFor="trend-period"
          className="text-sm font-medium text-slate-600"
          style={{ fontFamily: 'DM Sans, sans-serif' }}
        >
          Period
        </label>
        <ThemedSelect
          id="trend-period"
          value={months}
          onChange={(e) => setMonths(Number(e.target.value))}
          className="rounded-lg border border-slate-200 bg-surface px-3 py-2 text-sm text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition"
          style={{ fontFamily: 'DM Sans, sans-serif' }}
        >
          {PERIOD_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </ThemedSelect>
      </div>

      {/* Charts */}
      <ComplianceChart data={complianceData} loading={loadingCompliance} />
      <MaintenanceChart data={maintenanceData} loading={loadingMaintenance} />
      <NcrChart data={ncrData} loading={loadingNcr} />
    </div>
  );
};

export default ComplianceTrends;
